import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faAppleWhole,
  faBrain,
  faBriefcase,
  faBuildingUser,
  faCalendar,
  faChalkboardTeacher,
  faChalkboardUser,
  faEnvelope,
  faLocationDot,
  faPrint,
  faShieldHeart,
  faUser,
  faUserGroup,
  faUserNurse,
  faUserTie,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { checkIcon, plusIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { head, some } from 'lodash';
import { combineLatest, Observable, of, startWith, takeUntil } from 'rxjs';
import { ShmSaveRequest } from 'src/app/modules/evaluations/models/create-shm-package/shm-save.model';
import { EvaluationService } from 'src/app/modules/evaluations/services/evaluation.service';
import { iepSections } from 'src/app/modules/iep/iep-document/constants/iep-sections';
import { IEPService } from 'src/app/modules/iep/iep-document/services/iep.services';
import {
  ReferralInfo,
  ReferralStatusModel,
} from 'src/app/modules/referrals/interfaces/referrals.model';
import { ReferenceService } from 'src/app/services/reference.service';
import { StaffSearchService } from 'src/app/services/staff-search.service';
import { StudentService } from 'src/app/services/student.service';
import { AbstractForm } from 'src/app/shared/components/abstract-form';
import { MeetingSettingType } from 'src/app/shared/enums/meeting-setting-type';
import { MeetingType } from 'src/app/shared/enums/meeting-type';
import { Permissions } from 'src/app/shared/enums/permissions';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import { ScheduleMeetingReferenceData } from 'src/app/shared/interfaces/form/schedule-meetiing-reference-data.model';
import { ISedmsFormModel } from 'src/app/shared/interfaces/form/sedms-form-model';
import { MeetingMember } from 'src/app/shared/interfaces/meeting-member';
import { MeetingRole } from 'src/app/shared/interfaces/meeting-role';
import { MeetingRoleMap } from 'src/app/shared/interfaces/meeting-role-map';
import { ParentsInfo } from 'src/app/shared/interfaces/student';
import { Permission } from 'src/app/shared/models/permission';
import { AddScheduleMeetingFormModel } from 'src/app/shared/models/schedule-meeting-models/schedule-meeting-formmodel-model';
import { ScheduleMeetingRequestModel } from 'src/app/shared/models/schedule-meeting-models/schedule-meeting-request-model';
import { SubmitScheduleMeetingModel } from 'src/app/shared/models/schedule-meeting-models/submit-schedule-meeting-model';
import { User } from 'src/app/shared/models/user';
import { AuthStore } from 'src/app/stores/auth.store';
import {
  ADDITIONAL_REEVAL,
  ADDITIONAL_REEVALUATION,
  AMMEND_IEP,
  AMMENDMENT,
  ANNUAL_REVIEW,
  CHAIRPERSON,
  CREATE_ANNUAL_IEP_MEETING,
  GENERAL_EDUCATION_INTERVENTION_MEETING,
  INDIVIDUALIZED_EDUCATION_PLAN_MEETING,
  INITIAL,
  MANDATED_THREE_YEAR,
  PARENT,
  ParentTypeLabel,
  PAST_DATE,
  POSTAL_MAIL,
  PRINCIPAL,
  PSYCHOLOGIST,
  RECONVENE,
  RECONVENE_IEP_MEETING,
  RECONVENE_MEETING,
  REEVALUATION,
  RELATED_SERVICES,
  REPRESENTATIVE,
  RESCHEDULE_MEETING,
  SCHEDULE_EVALUATIONS_MEETING,
  SCHEDULE_MEETING,
  SCHEDULE_MEETING_CONSTANTS,
  SCHEDULE_SOCIAL_HISTORY_MEETING,
  SEVEN_DAYS_BEFORE_REQUIRED_START_DATE,
  SIXTY_DAYS_FROM_INITIAL_CONSENT_DATE,
  SIXTY_DAYS_FROM_TODAY_DATE,
  SOCIAL,
  SPECIAL,
  SUCPQ,
  TEACHER,
  VIEW_NOTICE_MEETING,
} from './schedule-meeting-constants';

import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { MeetingAttendee } from 'src/app/shared/interfaces/attendee';
import { ConsentDateResponse } from 'src/app/shared/interfaces/consent-date-response';
import { IMeetingDetails } from 'src/app/shared/interfaces/meeting-details';
import { StaffInfo } from 'src/app/shared/interfaces/staff-info';
import { StudentProfile } from 'src/app/shared/interfaces/student-profile';
import { validateEmail } from 'src/app/shared/validators/validator';

import { AttendeesList } from 'src/app/shared/models/schedule-meeting-models/submit-attendeesList-model';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { IepDetailsResponse } from '../../iep/iep-document/interfaces/previous-iep-details';
import { MeetingAttendeeDetail } from '../interface/meeting-attendee-detail';

@Component({
  selector: 'app-schedule-meeting',
  templateUrl: './schedule-meeting.component.html',
  styleUrls: ['./schedule-meeting.component.scss'],
  standalone: false,
})
export class ScheduleMeetingComponent extends AbstractForm<
  ScheduleMeetingRequestModel,
  unknown,
  Record<string, IReferenceData[]>
> {
  form: FormGroup;
  formModel: ISedmsFormModel = AddScheduleMeetingFormModel;
  @Input() referralSk: string;
  @Input() meetingSk: string;
  @Input() nycIds: string;
  @Input() override nycId: string;
  @Input() regComplianceDate: Date | string;
  @Input() meetingType: MeetingType;
  @Input() meetingRoster: MeetingRole[];
  @Output() closeEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Input() override viewMode: boolean;
  @Input() caseStage: string;
  @Input() actionText: string;
  dataList: StaffInfo[];
  // data
  referral: ReferralInfo;
  studentProfile!: StudentProfile;
  dueDate: Date;
  user: User;
  scheduleMeetingForm: FormGroup;
  addAttendeeForm: FormGroup;
  allPossibleAttendees: MeetingMember[] = [];
  EXTERNAL_ROLE: string = SCHEDULE_MEETING_CONSTANTS.EXTERNAL_ROLE;
  PARENT_ROLE: string = SCHEDULE_MEETING_CONSTANTS.PARENT_ROLE;
  meetingSettingTypeENUM: typeof MeetingSettingType = MeetingSettingType;
  public checked: boolean = true;

  // Additional Attendees
  meetingRolesArray: MeetingRole[];
  meetingRoleMap: MeetingRoleMap = {};
  meetingRoleMapUnassigned: MeetingRoleMap = {};
  meetingRolesArrayUnassigned: MeetingRole[];
  isMeetingTypeAnual: boolean = false;
  // reference
  meetingTypeEnum: typeof MeetingType = MeetingType;
  statuses: typeof ReferralStatusModel = ReferralStatusModel;
  state!: IReferenceData[];

  // UI
  confirmationDialog: boolean = false;
  menuTitle: string;
  showPrintDocs: boolean = false;
  showElectronicDeliveryMethods?: boolean = false;
  dateWarningDialogOpened: boolean = false;
  dateWarningDialogMessage: string;
  plusIcon: SVGIcon = plusIcon;
  checkIcon: SVGIcon = checkIcon;
  timePickerMin: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultMin;
  timePickerMax: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultMax;
  timePickerValue: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultValue;
  showStudentRequires: boolean;
  showAffectTheir: boolean;
  studentList: MeetingAttendeeDetail[] = [];
  hidePopup: boolean = false;

  parentList: MeetingAttendeeDetail[];
  generalEducationTeacherList: MeetingAttendeeDetail[];
  specialEducationTeacherList: MeetingAttendeeDetail[];
  schoolSocialWorkerList: MeetingAttendeeDetail[];
  schoolPsychologistsList: MeetingAttendeeDetail[];
  relatedServiceProviderList: MeetingAttendeeDetail[];
  districtRepresentativeList: MeetingAttendeeDetail[];
  filteredMeetingRoles: string[];
  attendeeLookupList: MeetingAttendeeDetail[][] = [];
  showParent: boolean;
  showstudent: boolean;
  showGeneralEducationTeacher: boolean;
  showSpecialEducationTeacher: boolean;
  showSchoolSocialWorker: boolean;
  showSchoolPsychologist: boolean;
  showRelatedServiceProvider: boolean;
  showdistrictRepresentative: boolean;
  showParentOptional: boolean;
  showstudentOptional: boolean;
  showGeneralEducationTeacherOptional: boolean;
  showSpecialEducationTeacherOptional: boolean;
  showSchoolSocialWorkerOptional: boolean;
  showSchoolPsychologistOptional: boolean;
  showRelatedServiceProviderOptional: boolean;
  showdistrictRepresentativeOPtional: boolean;
  principalList: MeetingAttendeeDetail[];
  showPrincipal: boolean;
  teacherList: MeetingAttendeeDetail[];
  showTeacher: boolean;
  displayFormat: string = 'MM/dd/yyyy';
  public format: FormatSettings = {
    displayFormat: 'MM/dd/yyyy',
    inputFormat: 'MM/dd/yyyy',
  };
  // UI
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  timePickerDefaultMin: Date = new Date(2000, 1, 1, 8, 0);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  timePickerDefaultMax: Date = new Date(2000, 1, 1, 20, 0);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  timePickerDefaultValue: Date = new Date(2000, 1, 1, 8, 0);
  showSpecialinstructions: boolean;
  showTranslationLanguage: boolean;
  isEmailValid: boolean;
  userDetails: User;
  loadSpinner: boolean;
  meetingAttendeesList: MeetingAttendee;
  isLoading: boolean = true;
  deliverySelected: boolean = false;
  originalScheduledDate: Date;
  minMeetingDate: Date | null = null;
  maxMeetingDate: Date;
  shmMinDate: Date | null | string = null;
  initialDate: string | null = null;
  submeetingType: number;
  minAnnualDate: Date;
  previousIepDetails: IepDetailsResponse;

  readonly annualMeetingTypeId: number = 98;
  readonly iepReconveneMeetingTypeId: number = 99;
  readonly iepMeetingTypeId: number = 4;
  readonly meetingModeId: number = 1; // Default mode for Schedule Meeting
  readonly teacherCount: number = 1;
  readonly principalCount: number = 1;
  readonly firstEmailIndex: number = 0;
  readonly singleParentCount: number = 1;
  readonly districtRepresentativeCount: number = 1;
  readonly schoolPsychologistCount: number = 1;
  readonly schoolSocialWorkerCount: number = 1;
  readonly specialEducationTeacherCount: number = 1;
  readonly generalEducationTeacherCount: number = 1;
  readonly parentCount: number = 1;
  readonly studentCount: number = 1;
  readonly minAttendeeCount: number = 1;
  readonly fullYearLength: number = 4;
  readonly hoursHour: number = 0;
  readonly hoursMinute: number = 0;
  readonly hoursSecond: number = 0;
  readonly hoursMillisecond: number = 0;
  readonly daysToAdd: number = 7;
  readonly daysToNextMeeting: number = 60;
  readonly meetingTypeGend: number = 1;
  readonly meetingTypeSHM: number = 3;
  get isIepMeeting(): boolean {
    return (
      this.meetingType === MeetingType.IEP ||
      this.meetingType === MeetingType.IEPReconvene ||
      this.meetingType === MeetingType.AnnualReview
    );
  }
  // SHM meeting dates are also bounded (by Date of Initial Referral instead of Date of Initial Consent)
  get isDateBoundedMeeting(): boolean {
    return this.isIepMeeting || this.meetingType === MeetingType.SHM;
  }
  private readonly meetingDateBoundaryValidator = (
    control: AbstractControl
  ): { minError?: boolean; maxError?: boolean } | null => {
    if (!this.isDateBoundedMeeting || !control?.value) {
      return null;
    }

    const controlDate: Date | null = this.toDate(control.value);
    if (!controlDate) {
      return null;
    }

    if (this.minMeetingDate && controlDate < this.minMeetingDate) {
      return { minError: true };
    }

    if (this.maxMeetingDate && controlDate > this.maxMeetingDate) {
      return { maxError: true };
    }

    return null;
  };

  constructor(
    private fb: FormBuilder,
    library: FaIconLibrary,
    private staffService: StaffSearchService,
    public studentService: StudentService,
    public iepService: IEPService<SubmitScheduleMeetingModel>,
    public activatedRoute: ActivatedRoute,
    public referenceService: ReferenceService,
    public referenceStore: ReferenceStore,
    private authStore: AuthStore,
    private evaluationService: EvaluationService<ShmSaveRequest>,
    notificationService: NotificationService,
    dialogService: DialogService,
    cdRef: ChangeDetectorRef,
    route: ActivatedRoute,
    formBuilder: FormBuilder,
    public dashboardService: DashboardService
  ) {
    super(cdRef, route, formBuilder, dialogService, notificationService);
    library.addIcons(
      faCalendar,
      faEnvelope,
      faUserGroup,
      faUser,
      faLocationDot,
      faBriefcase,
      faXmark,
      faPrint,
      faShieldHeart,
      faAppleWhole,
      faChalkboardUser,
      faUserNurse,
      faBrain,
      faBuildingUser,
      faUserTie,
      faChalkboardTeacher
    );
    this.authStore.UserInfo$?.pipe(
      takeUntil(this.componentDestroyed)
    ).subscribe((userInfo: User) => {
      this.userDetails = userInfo;
    });
    this.getAllApis();
    this.loadSpinner = true;
  }

  get entity$(): Observable<unknown> {
    return of({});
  }

  // For Getting Data from Dropdown
  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([this.referenceStore.language$]);
  }

  override get isDialog(): boolean {
    return true;
  }
  stopClickPropagation(event: Event): void {
    event.stopPropagation();
  }

  //get dropdown details data and entity get by Id Data
  initEntity(
    entityData: ScheduleMeetingRequestModel
  ): ScheduleMeetingRequestModel {
    return new ScheduleMeetingRequestModel(entityData);
  }
  getAllApis(): void {
    this.user = this.authStore.UserInfo;
  }
  initReferenceData([languages]: [
    IReferenceData[]
  ]): ScheduleMeetingReferenceData {
    return {
      languages,
    };
  }

  public override otherInitalizations(): void {
    const maxDate: Date = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    maxDate.setHours(23, 59, 59, 999);
    this.maxMeetingDate = maxDate;
    this.applyMeetingDateBoundaryValidator();
  }

  public override afterOnDestroy(): void {
    // afterOnDestroy
  }
  initWatches(): void {
    this.loadSpinner = true;
    //Show and hide Attendee based on Meeting Types
    // Load DropDown Data
    this.referenceStore.state$.subscribe((response: IReferenceData[]) => {
      this.state = response;
    });
    let meetingEnum: MeetingSettingType;
    if (
      this.actionText === SCHEDULE_MEETING ||
      this.actionText === RESCHEDULE_MEETING ||
      this.actionText === VIEW_NOTICE_MEETING ||
      this.actionText === RECONVENE_IEP_MEETING ||
      this.actionText === AMMEND_IEP
    ) {
      switch (this.caseStage) {
        case INITIAL:
          meetingEnum = this.meetingSettingTypeENUM.Initial;
          if (this.meetingType === this.annualMeetingTypeId) {
            this.isMeetingTypeAnual = true;
          }
          break;
        case REEVALUATION:
          meetingEnum = this.meetingSettingTypeENUM.Reevaluation;
          break;
        case AMMENDMENT:
          meetingEnum = this.meetingSettingTypeENUM.Amendment;
          break;
        case ADDITIONAL_REEVALUATION:
        case ADDITIONAL_REEVAL:
          meetingEnum = this.meetingSettingTypeENUM.AdditionalReevaluation;
          break;
        case MANDATED_THREE_YEAR:
          meetingEnum = this.meetingSettingTypeENUM.Mandated3Year;
          break;
        case ANNUAL_REVIEW:
          meetingEnum = this.meetingSettingTypeENUM.Annual;
          this.isMeetingTypeAnual = true;
          break;
        case RECONVENE:
          meetingEnum = this.meetingSettingTypeENUM.Reconvene;
      }
    } else if (
      this.actionText === CREATE_ANNUAL_IEP_MEETING ||
      this.meetingType === this.annualMeetingTypeId
    ) {
      meetingEnum = this.meetingSettingTypeENUM.Annual;
      this.isMeetingTypeAnual = true;
    }

    if (
      this.meetingType === this.iepMeetingTypeId ||
      this.meetingType === this.iepReconveneMeetingTypeId ||
      this.meetingType === this.annualMeetingTypeId
    ) {
      this.referenceService
        .getAttendeesList(this.referralSk, meetingEnum)
        .subscribe((attendeesResponse: MeetingAttendee) => {
          this.meetingAttendeesList = attendeesResponse;
        });
    }
    this.studentService
      .studentProfile(this.nycIds)
      .subscribe((response: StudentProfile) => {
        this.studentProfile = response;
        if (this.studentProfile) {
          this.studentStaffSearchDetails();
          this.bindReceptionData();
          this.setMeetingLocationDefaults();
          this.bindRecipientEmail();
          this.getConsentDate();
          this.checkStudentProfileValidity();

          this.iepService
            .getPreviousIepDetails(this.studentProfile.studentSk)
            .subscribe((res: IepDetailsResponse) => {
              this.previousIepDetails = res;
              if (this.actionText === CREATE_ANNUAL_IEP_MEETING) {
                // this.setMinAnnualDateValidator();
                this.setMinimumMeetingDateValidator();
              }
            });
        } else {
          this.handleStudentInfoError();
        }
      });
    this.AddAttendee();
    this.bindContactDetails();
    this.valueChangeForMeetingDate();
    this.valueChangeForDeliveryMethod();
  }

  handleStudentInfoLoaded(): void {
    this.checkStudentProfileValidity();
  }

  handleStudentInfoError(): void {
    this.isLoading = true; // Keep loading visible if there's an error
  }

  checkStudentProfileValidity(): void {
    if (
      this.studentProfile?.firstName === null ||
      this.studentProfile?.lastName === null
    ) {
      this.isLoading = true;
    } else {
      this.isLoading = false; // Hide loader if data is valid
    }
  }

  studentStaffSearchDetails(): void {
    const district: number =
      this.studentProfile?.homeDistrict !== null
        ? +this.studentProfile?.homeDistrict
        : null;
    this.staffService
      .getAllStaffForSchool(this.studentProfile?.schoolDBN, district)
      .subscribe((res: StaffInfo[]) => {
        //TODO:We need to add types for the api endpoints, once added we need to add the types to the subscribe method
        this.dataList = res;
        if (res) {
          this.showAndHideAttendees();
          this.showAndHideManualAttendees();
          this.getRefernceLookupForStudent();
          if (this.showPrincipal) {
            this.getRefernceLookupForPrincipal();
          }
          this.getRefernceLookupForParent();
          this.getRefernceLookupForGeneralEducationTeacher();
          this.getRefernceLookupForSpecialEducationTeacher();
          this.getRefernceLookupForSchoolSocialWorker();
          this.getRefernceLookupForSchoolPsychologists();
          this.getRefernceLookupForRelatedServiceProvider();
          this.getRefernceLookupForDistrictRepresentative();
          this.getRefernceLookupForTeacher();
          this.patchMeetingDetails();
        }
        this.loadSpinner = false;
      });
  }
  patchMeetingDetails(): void {
    if (this.meetingSk) {
      this.iepService
        .getMeetingDetails(this.meetingSk, iepSections.meeting)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((data: IMeetingDetails) => {
          this.submeetingType = data?.SubMeetingType;
          this.hidePopup = true;
          const fromJsonData: ScheduleMeetingRequestModel = JSON.parse(
            data.formJson
          );
          this.setMinimumMeetingDateValidator(data.CurrScheduleDt);
          const formModel: ScheduleMeetingRequestModel =
            new ScheduleMeetingRequestModel(fromJsonData);
          this.form.patchValue(formModel);
          const attendeeArray: FormArray = this.form.get(
            'attendee'
          ) as FormArray;
          attendeeArray.clear();
          if (formModel?.attendee) {
            formModel?.attendee?.forEach((att: AttendeesList) => {
              attendeeArray?.push(
                this.fb.group({
                  selectedAttendee: [att?.selectedAttendee || ''],
                  role: [att?.role || ''],
                  email: [att?.email || ''],
                  isRequired: [att?.isRequired || false],
                })
              );
            });
          }
          if (this.viewMode) {
            attendeeArray.disable();
          }
          this.originalScheduledDate = data.OrigScheduleDt;
        });
    }
  }

  public setMinAnnualDateValidator(): void {
    if (this.previousIepDetails?.minAnnualDate) {
      this.minAnnualDate = new Date(this.previousIepDetails.minAnnualDate);
      const meetingDateControl: AbstractControl = this.form.get('meetingDate');

      meetingDateControl?.setValidators([
        Validators.required,
        (control: AbstractControl) => {
          return control.value && new Date(control.value) < this.minAnnualDate
            ? { minDate: true }
            : null;
        },
      ]);

      meetingDateControl?.updateValueAndValidity();
    }
  }

  public setMinimumMeetingDateValidator(meetingDate?: Date): void {
    if (this.meetingType === MeetingType.IEPReconvene) {
      this.minMeetingDate = this.toDate(meetingDate);
    } else if (
      (this.submeetingType === this.meetingSettingTypeENUM.Annual ||
        this.meetingType === MeetingType.AnnualReview) &&
      this.previousIepDetails?.minAnnualDate
    ) {
      this.minMeetingDate = this.toDate(this.previousIepDetails.minAnnualDate);
    }
    const meetingDateControl: FormControl = this.form.get(
      'meetingDate'
    ) as FormControl;
    meetingDateControl?.setValidators([
      ...(meetingDateControl.validator ? [meetingDateControl.validator] : []), // Preserve existing validators (ex: min and max)
      Validators.required,
      (control) => {
        const controlDate: Date | null = this.toDate(control.value);
        return this.isIepMeeting &&
          controlDate &&
          this.minMeetingDate &&
          controlDate < this.minMeetingDate
          ? { minDate: true }
          : null;
      },
    ]);
    meetingDateControl?.updateValueAndValidity();
  }

  private applyMeetingDateBoundaryValidator(): void {
    const meetingDateControl: AbstractControl = this.form?.get('meetingDate');
    if (!meetingDateControl) {
      return;
    }

    meetingDateControl.addValidators(this.meetingDateBoundaryValidator);
    meetingDateControl.updateValueAndValidity();
  }

  private toDate(value: Date | string): Date | null {
    if (!value) {
      return null;
    }

    const date: Date = value instanceof Date ? new Date(value) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  valueChangeForMeetingDate(): void {
    this.form
      .get('meetingDate')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.form.get('teacher.teacherName')?.value)
      )
      .subscribe((value: Date) => {
        if (value && !this.viewMode) {
          if (this.hidePopup === false) {
            this.onDateSelected(value);
          } else {
            this.hidePopup = false;
          }
        }
      });
  }

  valueChangeForDeliveryMethod(): void {
    this.form
      .get('postalMail')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.form.get('postalMail')?.value)
      )
      .subscribe((value: boolean) => {
        if (value || this.form.get('parentPortal')?.value) {
          this.deliverySelected = true;
        } else {
          this.deliverySelected = false;
        }
      });

    this.form
      .get('parentPortal')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.form.get('parentPortal')?.value)
      )
      .subscribe((value: boolean) => {
        if (value || this.form.get('postalMail')?.value) {
          this.deliverySelected = true;
        } else {
          this.deliverySelected = false;
        }
      });
  }

  bindContactDetails(): void {
    this.form.patchValue({
      contactName: `${this.user.firstName} ${this.user.lastName}`,
    });
  }
  showAndHideAttendees(): void {
    // Set Modal Title
    switch (this.meetingType) {
      case this.meetingTypeEnum.SHM:
        this.filteredMeetingRoles = [
          ...new Set(
            this.dataList
              ?.filter((role: StaffInfo) =>
                SCHEDULE_MEETING_CONSTANTS.SHM_ALLOWED_ADDITIONAL_ATTENDEE_ROLES.includes(
                  role.title
                )
              )
              .map((role: StaffInfo) => role.title)
          ),
        ];

        this.menuTitle = SCHEDULE_SOCIAL_HISTORY_MEETING;
        this.showParent = true;
        this.showSchoolSocialWorker = true;
        break;
      case this.meetingTypeEnum.GenEd:
        this.filteredMeetingRoles = [
          ...new Set(
            this.dataList
              ?.filter((role: StaffInfo) =>
                SCHEDULE_MEETING_CONSTANTS.GEN_ED_ALLOWED_ADDITIONAL_ATTENDEE_ROLES.includes(
                  role.title
                )
              )
              .map((role: StaffInfo) => role.title)
          ),
        ];

        this.showPrincipal = true;
        this.showParent = true;
        this.showTeacher = true;
        this.menuTitle = GENERAL_EDUCATION_INTERVENTION_MEETING;
        break;
      case this.meetingTypeEnum.EVL:
        this.filteredMeetingRoles = [
          ...new Set(
            this.dataList
              ?.filter((role: StaffInfo) =>
                SCHEDULE_MEETING_CONSTANTS.SHM_ALLOWED_ADDITIONAL_ATTENDEE_ROLES.includes(
                  role.title
                )
              )
              .map((role: StaffInfo) => role.title)
          ),
        ];

        this.menuTitle = SCHEDULE_EVALUATIONS_MEETING;
        this.showParent = true;
        this.showSchoolSocialWorker = true;
        break;
      case this.meetingTypeEnum.IEP:
      case this.meetingTypeEnum.IEPReconvene:
      case this.meetingTypeEnum.AnnualReview:
        this.filteredMeetingRoles = [
          ...new Set(
            this.dataList
              ?.filter((role: StaffInfo) =>
                SCHEDULE_MEETING_CONSTANTS.IEP_ALLOWED_ADDITIONAL_ATTENDEE_ROLES.includes(
                  role.title
                )
              )
              .map((role: StaffInfo) => role.title)
          ),
        ];

        this.menuTitle = INDIVIDUALIZED_EDUCATION_PLAN_MEETING;
        this.showParent = this.meetingAttendeesList?.isParent;
        this.showGeneralEducationTeacher =
          this.meetingAttendeesList?.isGeneralEducationalTeacher;
        this.showSpecialEducationTeacher =
          this.meetingAttendeesList.isSpecialEducationalTeacher;
        this.showSchoolSocialWorker =
          this.meetingAttendeesList.isSchoolSocialWorker;
        this.showSchoolPsychologist =
          this.meetingAttendeesList.isSchoolPsychologist;
        this.showRelatedServiceProvider =
          this.meetingAttendeesList.isRelatedServiceProvider;
        this.showdistrictRepresentative =
          this.meetingAttendeesList.isDistrictRepresentative;
        //Show under optional
        this.showParentOptional = !this.meetingAttendeesList?.isParent;
        this.showGeneralEducationTeacherOptional =
          !this.meetingAttendeesList.isGeneralEducationalTeacher;
        this.showSpecialEducationTeacherOptional =
          !this.meetingAttendeesList.isSpecialEducationalTeacher;
        this.showSchoolSocialWorkerOptional =
          !this.meetingAttendeesList.isSchoolSocialWorker;
        this.showSchoolPsychologistOptional =
          !this.meetingAttendeesList.isSchoolPsychologist;
        this.showRelatedServiceProviderOptional =
          !this.meetingAttendeesList.isRelatedServiceProvider;
        this.showdistrictRepresentativeOPtional =
          !this.meetingAttendeesList.isDistrictRepresentative;
        break;
    }
  }

  showAndHideManualAttendees(): void {
    if (
      this.actionText === SCHEDULE_MEETING ||
      this.actionText === RESCHEDULE_MEETING ||
      this.actionText === VIEW_NOTICE_MEETING ||
      this.actionText === RECONVENE_MEETING ||
      this.actionText === AMMEND_IEP
    ) {
      this.showParent = true;
      this.showdistrictRepresentative = true;
      if (this.caseStage !== ANNUAL_REVIEW) {
        this.showSchoolPsychologist = true;
      }
      if (
        this.caseStage === ANNUAL_REVIEW ||
        this.caseStage === REEVALUATION ||
        this.caseStage === ADDITIONAL_REEVALUATION ||
        this.caseStage === ADDITIONAL_REEVAL ||
        this.caseStage === MANDATED_THREE_YEAR
      ) {
        if (this.previousIepDetails) {
          const res: IepDetailsResponse = this.previousIepDetails;
          if (res?.hasRS || res?.hasSCIS || res?.hasSEprogram) {
            this.showSpecialEducationTeacher = true;
          }
          if (!res.hasRS && !res.hasSCIS && !res.hasSEprogram) {
            this.showRelatedServiceProvider = true;
          }
          if (res.hasSCIS) {
            this.showGeneralEducationTeacher = true;
          }
        }
      }
    }
  }

  getRefernceLookupForTeacher(): void {
    this.teacherList = this.dataList
      .filter((item: StaffInfo) => item.title?.toLowerCase()?.includes(TEACHER))
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
      }));
    if (this.teacherList.length === this.teacherCount) {
      const data: MeetingAttendeeDetail = head(this.teacherList); // Get the first item from the array
      this.form.get('teacher.teacherName').setValue(data);
    }
    this.form
      .get('teacher.teacherName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.form.get('teacher.teacherName')?.value)
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form.get('teacher.teacherEmail').setValue(value?.email ?? '');
        }
      });
  }
  getRefernceLookupForPrincipal(): void {
    this.principalList = this.dataList
      .filter((item: StaffInfo) => item.title?.toLowerCase() === PRINCIPAL)
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
      }));
    if (this.principalList.length === this.principalCount) {
      const data: MeetingAttendeeDetail = head(this.principalList); // Get the first item from the array
      this.form.get('principal.principalName').setValue(data);
    }
    this.form
      .get('principal.principalName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.form.get('principal.principalName')?.value)
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form
            .get('principal.principalEmail')
            .setValue(value?.email ?? '');
        }
      });
  }

  showAttendeeLookupList(arrayIndex: number): void {
    const Value: string = (this.form.get('attendee') as FormArray)
      .at(arrayIndex)
      .get('role').value;
    if (Value) {
      const selectedRole: string = Value;

      this.attendeeLookupList[arrayIndex] = this.dataList
        .filter(
          (item: StaffInfo) => item.title && item.title.includes(selectedRole)
        )
        .map((item: StaffInfo) => ({
          email: item.email,
          id: item.loginId,
          description: `${item.firstName} ${item.lastName}`,
        }));
    }
  }
  getEmailData(arrayIndex: number): void {
    const Value: string = (this.form.get('attendee') as FormArray)
      .at(arrayIndex)
      .get('role').value;
    if (Value) {
      const selectedName: string = Value;
      const email: StaffInfo[] = this.dataList
        .filter((item: StaffInfo) => item.title.includes(selectedName))
        .map((item: StaffInfo) => ({
          email: item.email,
          id: item.loginId,
          description: `${item.firstName} ${item.lastName}`,
        }));
      if (email[this.firstEmailIndex]?.email) {
        (this.form.get('attendee') as FormArray)
          .at(arrayIndex)
          .get('email')
          .setValue(email[this.firstEmailIndex]?.email);
      }
    }
  }
  setMeetingLocationDefaults(): void {
    const addressParts: string[] = [
      this.studentProfile?.enrollment?.address?.streetAddress1,
      this.studentProfile?.enrollment?.address?.streetAddress2,
      this.studentProfile?.enrollment?.address?.city,
      this.studentProfile?.enrollment?.address?.stateName,
      this.studentProfile?.enrollment?.address?.zipCode,
    ].filter(
      (part: string | null | undefined): part is string =>
        !!part && part.trim().length > 0
    );

    this.form.patchValue({
      meetingStreetAddress1: addressParts.join(' '),
    });
  }
  bindRecipientEmail(): void {
    this.form
      .get('selectedRecipient')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.form.get('selectedRecipient')?.value)
      )
      .subscribe((value: StaffInfo) => {
        if (value) {
          this.showElectronicDeliveryMethods = true;
          this.form
            .get('recipientEmailAddress')
            .setValue(value?.email?.trim() ?? '');
          this.form.get('recipientPhoneNumber').setValue(value?.workPhone);
          this.form
            .get('recipientStreetAddress1')
            ?.setValue(value?.streetAddress1);
          this.form
            .get('recipientStreetAddress2')
            ?.setValue(value?.streetAddress2);
          this.form.get('recipientCity').setValue(value?.city);
          this.form.get('recipientState').setValue(value?.state);
          this.form.get('recipientZip').setValue(value?.zip);
        }
      });
  }
  getConsentDate(): void {
    this.evaluationService
      .getConsentDate(this.referralSk)
      .subscribe((res: ConsentDateResponse) => {
        this.initialDate =
          this.meetingType === MeetingType.SHM ? res.initialDate ?? null : null;
        const consentDate: string = res.consentDate;
        if (consentDate) {
          this.form.get('consentDate').setValue(new Date(consentDate));
        } else {
          this.form.get('consentDate').setValue(null);
        }
        if (
          this.meetingType === MeetingType.SHM ||
          this.meetingType === MeetingType.IEP
        ) {
          // SHM's meeting date is bounded by the Date of Initial Referral; IEP by the Date of Initial Consent
          const minDateSource: string =
            this.meetingType === MeetingType.SHM
              ? res.initialDate
              : consentDate;
          const parsedMinDate: Date | null = this.toDate(minDateSource);
          if (parsedMinDate) {
            parsedMinDate.setHours(0, 0, 0, 0);
          }
          this.minMeetingDate = parsedMinDate;
          this.shmMinDate = this.minMeetingDate;
          this.applyMeetingDateBoundaryValidator();
        }
      });
  }
  bindReceptionData(): void {
    // Find the primary contact parent
    const primaryContact: ParentsInfo =
      this.studentProfile?.parentDetails?.find((parent: ParentsInfo) => {
        return parent.isPrimary;
      });

    // If the primary contact exists, add to allPossibleAttendees
    if (primaryContact) {
      primaryContact.fullName = `${primaryContact.firstName} ${primaryContact.lastName}`;
      this.allPossibleAttendees.push({
        firstName: primaryContact.firstName ?? '',
        lastName: primaryContact.lastName ?? '',
        fullName: primaryContact.fullName ?? '',
        displayName: `${primaryContact ? `(${ParentTypeLabel.Primary})` : ''} ${
          primaryContact.fullName ?? ''
        }`,
        email: primaryContact.emailAddress ?? '',
        consentsToElectronicCommunication:
          primaryContact.consentsToElectronicCommunication,
        workPhone: primaryContact.cellPhone ?? '',
        title: PARENT.toUpperCase(),
        loginId: primaryContact.parentSk ?? '',
        isRequired: false,
        location: '',
        streetAddress1: primaryContact.address?.streetAddress1 ?? '',
        streetAddress2: primaryContact.address?.streetAddress2 ?? '',
        state: primaryContact.address?.stateName ?? '',
        city: primaryContact.address?.city ?? '',
        zip: primaryContact.address?.zipCode ?? '',
      });

      const data: MeetingMember = this.allPossibleAttendees[0];
      this.form.get('selectedRecipient').setValue(data);
    }
  }
  getRefernceLookupForDistrictRepresentative(): void {
    this.districtRepresentativeList = this.dataList
      .filter(
        (item: StaffInfo) =>
          !!item.loginId &&
          (item.title?.toLowerCase()?.includes(REPRESENTATIVE) ||
            item.title?.toLowerCase()?.includes(CHAIRPERSON) ||
            item.titleCode?.toUpperCase()?.includes(SUCPQ))
      )
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
        workPhone: item.workPhone,
      }));
    if (
      this.districtRepresentativeList.length ===
      this.districtRepresentativeCount
    ) {
      const data: MeetingAttendeeDetail = head(this.districtRepresentativeList); // Get the first item from the array
      this.form
        .get('districtRepresentative.districtRepresentativerName')
        .setValue(data);
    }
    this.form
      .get('districtRepresentative.districtRepresentativerName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(
          this.form.get('districtRepresentative.districtRepresentativerName')
            ?.value
        )
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value && value?.email) {
          this.form
            .get('districtRepresentative.districtRepresentativerEmail')
            .setValue(value?.email);
        }
      });
  }
  getRefernceLookupForRelatedServiceProvider(): void {
    this.relatedServiceProviderList = this.dataList
      .filter(
        (item: StaffInfo) =>
          item.title?.toLowerCase()?.includes(`(${RELATED_SERVICES})`) &&
          !!item.loginId
      )
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
        workPhone: item.workPhone,
      }));
    this.form
      .get('relatedServiceProvider.relatedServiceProviderName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(
          this.form.get('relatedServiceProvider.relatedServiceProviderName')
            ?.value
        )
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form
            .get('relatedServiceProvider.relatedServiceProviderEmail')
            .setValue(value?.email ?? '');
        }
      });
  }
  getRefernceLookupForSchoolPsychologists(): void {
    this.schoolPsychologistsList = this.dataList
      .filter(
        (item: StaffInfo) =>
          item.title?.toLowerCase()?.includes(PSYCHOLOGIST) && !!item.loginId
      )
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
        workPhone: item.workPhone,
      }));
    if (this.schoolPsychologistsList.length === this.schoolPsychologistCount) {
      const data: MeetingAttendeeDetail = head(this.schoolPsychologistsList); // Get the first item from the array
      this.form.get('schoolPsychologist.schoolPsychologistName').setValue(data);
    }
    this.form
      .get('schoolPsychologist.schoolPsychologistName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(
          this.form.get('schoolPsychologist.schoolPsychologistName')?.value
        )
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form
            .get('schoolPsychologist.schoolPsychologistEmail')
            .setValue(value?.email ?? '');
        }
      });
  }
  getRefernceLookupForSchoolSocialWorker(): void {
    this.schoolSocialWorkerList = this.dataList
      .filter(
        (item: StaffInfo) =>
          item.title?.toLowerCase()?.includes(SOCIAL) && !!item.loginId
      )
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
        workPhone: item.workPhone,
      }));
    if (this.schoolSocialWorkerList.length === this.schoolSocialWorkerCount) {
      const data: MeetingAttendeeDetail = head(this.schoolSocialWorkerList); // Get the first item from the array
      this.form.get('schoolSocialWorker.schoolSocialWorkerName').setValue(data);
    }
    this.form
      .get('schoolSocialWorker.schoolSocialWorkerName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(
          this.form.get('schoolSocialWorker.schoolSocialWorkerName')?.value
        )
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form
            .get('schoolSocialWorker.schoolSocialWorkerEmail')
            .setValue(value?.email ?? '');
        }
      });
  }
  getRefernceLookupForSpecialEducationTeacher(): void {
    this.specialEducationTeacherList = this.dataList
      .filter(
        (item: StaffInfo) =>
          item.title?.toLowerCase()?.includes(SPECIAL) && !!item.loginId
      )
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
        workPhone: item.workPhone,
      }));
    if (
      this.specialEducationTeacherList.length ===
      this.specialEducationTeacherCount
    ) {
      const data: MeetingAttendeeDetail = head(
        this.specialEducationTeacherList
      ); // Get the first item from the array
      this.form
        .get('specialEducationTeacher.specialEducationTeacherName')
        .setValue(data);
    }

    this.form
      .get('specialEducationTeacher.specialEducationTeacherName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(
          this.form.get('specialEducationTeacher.specialEducationTeacherName')
            ?.value
        )
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form
            .get('specialEducationTeacher.specialEducationTeacherEmail')
            .setValue(value?.email ?? '');
        }
      });
  }
  getRefernceLookupForGeneralEducationTeacher(): void {
    this.generalEducationTeacherList = this.dataList
      .filter(
        (item: StaffInfo) =>
          item.title?.toLowerCase()?.includes(TEACHER) && !!item.loginId
      )
      .map((item: StaffInfo) => ({
        email: item.email,
        id: item.loginId,
        description: `${item.firstName} ${item.lastName}`,
        workPhone: item.workPhone,
      }));
    if (
      this.generalEducationTeacherList.length ===
      this.generalEducationTeacherCount
    ) {
      const data: MeetingAttendeeDetail = head(
        this.generalEducationTeacherList
      ); // Get the first item from the array
      this.form
        .get('generalEducationTeacher.generalEducationTeacherName')
        .setValue(data);
    }
    this.form
      .get('generalEducationTeacher.generalEducationTeacherName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(
          this.form.get('generalEducationTeacher.generalEducationTeacherName')
            ?.value
        )
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form
            .get('generalEducationTeacher.generalEducationTeacherEmail')
            .setValue(value?.email ?? '');
        }
      });
  }
  getRefernceLookupForParent(): void {
    this.parentList = this.studentProfile?.parentDetails?.map(
      (item: ParentsInfo) => ({
        email: item.emailAddress,
        id: item.parentSk,
        description: `${item.firstName} ${item.lastName}`,
      })
    );
    if (this.parentList?.length === this.parentCount) {
      const data: MeetingAttendeeDetail = head(this.parentList); // Get the first item from the array
      this.form.get('parent.parentName').setValue(data);
    }
    this.form
      .get('parent.parentName')
      ?.valueChanges.pipe(
        takeUntil(this.componentDestroyed),
        startWith(this.form.get('parent.parentName')?.value)
      )
      .subscribe((value: MeetingAttendeeDetail) => {
        if (value) {
          this.form
            .get('parent.parentEmail')
            .setValue(value?.email?.trim() ?? '');
        }
      });
  }
  getRefernceLookupForStudent(): void {
    const studentDetails: StudentProfile[] = [this.studentProfile];
    this.studentList = studentDetails.map((item: StudentProfile) => ({
      id: item.studentSk,
      description: `${item.firstName} ${item.lastName}`,
    }));
    if (this.studentList.length === this.studentCount) {
      const firstStudent: MeetingAttendeeDetail = head(this.studentList); // Get the first item from the array
      this.form.get('student.studentName').setValue(firstStudent);
    }
  }

  postCancelConfirmation(): void {
    // postCancelConfirmation
  }

  postSubmitConfirmation(): void {
    this.dashboardService.reloadDashboardSubject$.next(true);
    this.closeEmitter.next();
  }

  saveDetails(): Observable<ApiResponse<IMeetingDetails>> {
    const scheduleMeetingData: SubmitScheduleMeetingModel =
      new SubmitScheduleMeetingModel({
        ...this.form.value,
        initialDate:
          this.meetingType === MeetingType.SHM ? this.initialDate : undefined,
      });
    scheduleMeetingData.studentSk = this.studentProfile?.studentSk;
    scheduleMeetingData.parentSk = this.referralSk;
    scheduleMeetingData.nycId = this.nycIds;
    scheduleMeetingData.meetingType = this.meetingType;
    scheduleMeetingData.personSk = this.user.personSk;
    scheduleMeetingData.origScheduleDt =
      this.originalScheduledDate ?? scheduleMeetingData.origScheduleDt;
    scheduleMeetingData.shmMinDate = new Date(this.shmMinDate);
    scheduleMeetingData.parentReqReschedule =
      this.meetingSk !== undefined &&
      (this.meetingType === this.meetingTypeGend ||
        this.meetingType === this.meetingTypeSHM)
        ? true
        : false;
    if (
      this.actionText === CREATE_ANNUAL_IEP_MEETING ||
      this.isMeetingTypeAnual
    ) {
      scheduleMeetingData.subMeetingType = this.meetingSettingTypeENUM.Annual;
    } else if (this.actionText === RECONVENE_IEP_MEETING) {
      scheduleMeetingData.subMeetingType =
        this.meetingSettingTypeENUM.Reconvene;
    }
    if (this.meetingSk) {
      scheduleMeetingData.meetingSk = this.meetingSk;
      return this.iepService.updateMeeting(
        scheduleMeetingData,
        iepSections.meeting
      );
    } else {
      return this.iepService.submitMeeting(
        scheduleMeetingData,
        iepSections.meeting
      );
    }
  }

  get canSaveDraft(): boolean {
    return !this.form.valid;
  }

  readonly canSubmit: boolean = false;

  newAttendee(): FormGroup {
    return this.fb.group({
      selectedAttendee: [''],
      role: [''],
      email: ['', validateEmail(false)],
      name: [''],
      isRequired: [''],
    });
  }

  AddAttendee(): void {
    const control: FormArray = this.form?.controls.attendee as FormArray;
    control.push(this.newAttendee());
    const attendeeControls: AbstractControl[] = control.controls;
    attendeeControls.forEach((attendee: AbstractControl) => {
      attendee.get('isRequired').valueChanges.subscribe((value: boolean) => {
        if (value === true) {
          attendee.get('email').setValidators([validateEmail(true)]);
          attendee.get('email').updateValueAndValidity();
          attendee.get('selectedAttendee').setValidators([Validators.required]);
          attendee.get('selectedAttendee').updateValueAndValidity();
          attendee.get('role').setValidators([Validators.required]);
          attendee.get('role').updateValueAndValidity();
        }
        if (value === false) {
          attendee.get('email').setValidators([validateEmail(false)]); // not required but validate email
          attendee.get('email').updateValueAndValidity();
          attendee
            .get('selectedAttendee')
            .removeValidators([Validators.required]);
          attendee.get('selectedAttendee').updateValueAndValidity();
          attendee.get('role').removeValidators([Validators.required]);
          attendee.get('role').updateValueAndValidity();
        }
      });
    });
  }

  removeAttendee(index: number): void {
    const control: FormArray = this.form?.controls.attendee as FormArray;
    if (control.length > this.minAttendeeCount) {
      control.removeAt(index);
    }
  }

  valueRoleChange(arrayIndex: number): void {
    const formArray: FormArray = this.form.get('attendee') as FormArray;
    const role: AbstractControl = formArray?.at(arrayIndex)?.get('role');
    const selectedAttendee: AbstractControl = formArray
      ?.at(arrayIndex)
      ?.get('selectedAttendee');
    if (role) {
      role.valueChanges
        .pipe(startWith(role.value))
        .subscribe((data: string) => {
          if (data) {
            selectedAttendee.setValidators([Validators.required]);
          } else {
            selectedAttendee.clearValidators();
          }
          selectedAttendee.updateValueAndValidity();
        });
    }
  }
  getControls(): AbstractControl[] {
    return (this.form?.get('attendee') as FormArray).controls;
  }

  get showScheduleBtn(): boolean {
    let showBtn: boolean = false;
    switch (this.meetingType) {
      case this.meetingTypeEnum.SHM:
        showBtn =
          some(
            this.userDetails.selectedRole?.userPermissions,
            (x: Permission) => x.permissionId === Permissions.CanScheduleSHM
          ) ||
          some(
            this.userDetails.selectedRole?.userPermissions,
            (x: Permission) => x.permissionId === Permissions.CanReScheduleSHM
          );
        break;
      case this.meetingTypeEnum.GenEd:
        showBtn = some(
          this.userDetails.selectedRole?.userPermissions,
          (x: Permission) => x.permissionId === Permissions.CanScheduleGEN
        );
        break;
      case this.meetingTypeEnum.IEP:
        showBtn =
          some(
            this.userDetails.selectedRole?.userPermissions,
            (x: Permission) =>
              x.permissionId === Permissions.CanScheduleIEPMeeting
          ) ||
          some(
            this.userDetails.selectedRole?.userPermissions,
            (x: Permission) =>
              x.permissionId === Permissions.CanRescheduleIEPMeeting
          );
        break;
      case this.meetingTypeEnum.IEPReconvene:
      case this.meetingTypeEnum.AnnualReview:
        showBtn = true;
        break;
    }
    return showBtn;
  }
  onDeliveryMethodChange(type: string, event: Event): void {
    const methodOfDeliveryArr: FormArray = this.scheduleMeetingForm.controls
      .deliveryMethod as FormArray;

    if ((event.target as HTMLInputElement).checked) {
      if (type === POSTAL_MAIL) {
        this.showPrintDocs = true;
      }

      methodOfDeliveryArr.push(new FormControl(type));
    } else {
      if (type === POSTAL_MAIL) {
        this.showPrintDocs = false;
      }

      const index: number = methodOfDeliveryArr.controls.findIndex(
        (x: AbstractControl) => x.value === type
      );
      methodOfDeliveryArr.removeAt(index);
    }
  }

  get attendeesFormArray(): FormArray {
    return this.scheduleMeetingForm.controls.attendees as FormArray;
  }

  ////////////////////////////// DatePicker //////////////////////////////

  closeDateWarningDialog(): void {
    this.dateWarningDialogOpened = false;
  }

  dateWarningAction(proceed: boolean): void {
    if (!proceed) {
      this.form.patchValue({
        meetingDate: null,
      });
    }

    this.dateWarningDialogOpened = false;
    this.dateWarningDialogMessage = '';
  }
  public disabledDates: (date: Date) => boolean = (date: Date): boolean => {
    const startDate: Date = new Date();
    startDate.setHours(
      this.hoursHour,
      this.hoursMinute,
      this.hoursSecond,
      this.hoursMillisecond
    );
    //startDate.setDate(startDate.getDate() + 7);
    return date < startDate;
  };
  isFullYearEntered(date: Date): boolean {
    return date.getFullYear().toString().length === this.fullYearLength;
  }
  public onDateSelected(value: Date): void {
    if (!value) return;

    const selectedDate: Date = new Date(value);
    selectedDate.setHours(
      this.hoursHour,
      this.hoursMinute,
      this.hoursSecond,
      this.hoursMillisecond
    );

    const today: Date = new Date();
    today.setHours(
      this.hoursHour,
      this.hoursMinute,
      this.hoursSecond,
      this.hoursMillisecond
    );

    let startDate: Date = this.form.get('startDate')?.value || today;
    startDate = new Date(startDate);
    startDate.setHours(
      this.hoursHour,
      this.hoursMinute,
      this.hoursSecond,
      this.hoursMillisecond
    );
    startDate.setDate(today.getDate() + this.daysToAdd);

    const meeting60Days: Date = new Date(today);
    meeting60Days.setDate(today.getDate() + this.daysToNextMeeting);

    let iepMeetingLimitDate: Date;
    const consentDate: Date = this.form.get('consentDate')?.value;

    if (consentDate) {
      iepMeetingLimitDate = new Date(consentDate);
      iepMeetingLimitDate.setHours(
        this.hoursHour,
        this.hoursMinute,
        this.hoursSecond,
        this.hoursMillisecond
      );
      iepMeetingLimitDate.setDate(
        iepMeetingLimitDate.getDate() + this.daysToNextMeeting
      );
    } else {
      iepMeetingLimitDate = new Date(meeting60Days);
    }

    this.dateWarningDialogOpened = false;
    this.dateWarningDialogMessage = '';

    if (this.isFullYearEntered(selectedDate)) {
      if (selectedDate < startDate && selectedDate >= today) {
        this.dateWarningDialogMessage = SEVEN_DAYS_BEFORE_REQUIRED_START_DATE;
        this.dateWarningDialogOpened = true;
      } else if (selectedDate > meeting60Days) {
        this.dateWarningDialogMessage = SIXTY_DAYS_FROM_TODAY_DATE;
        this.dateWarningDialogOpened = true;
      } else if (
        selectedDate > iepMeetingLimitDate &&
        this.caseStage === this.meetingSettingTypeENUM.Initial.toString()
      ) {
        this.dateWarningDialogMessage = SIXTY_DAYS_FROM_INITIAL_CONSENT_DATE;
        this.dateWarningDialogOpened = true;
      } else if (selectedDate >= today) {
        this.dateWarningDialogMessage = PAST_DATE;
      }
    }
  }

  toggleConsent(event: Event): void {
    if ((event.target as HTMLInputElement).checked) {
      this.showElectronicDeliveryMethods = true;
    } else {
      this.showElectronicDeliveryMethods = false;
    }
  }

  onCancelSHM(): void {
    this.confirmationDialog = false;
    this.dashboardService.reloadDashboardSubject$.next(true);
    this.closeEmitter.next();
  }

  openConfirmationDialog(): void {
    if (!this.viewMode) this.confirmationDialog = true;
    else this.onCancelSHM();
  }

  closeConfirmationDialog(): void {
    this.confirmationDialog = false;
  }

  getValidateValidationMessage(
    formControlName: string,
    arrayIndex?: number
  ): string {
    const control: AbstractControl | null = (
      this.form.get('attendee') as FormArray
    )
      .at(arrayIndex)
      .get(formControlName);
    return control?.errors?.validateEmail?.message;
  }
  ////////////////////////////// END Schedule/Cancel //////////////////////////////
}
